'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from '@eliadmin/core/src/components/image/image-wrapped';
import React from 'react';

import type { TDynamicData } from '../../types/dynamic-data';

import { Carousel } from '@eliadmin/core/src/components/ui/carousel';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Heading } from '../ui/heading';
import { Paragraph } from '../ui/paragraph';
import { LuMinus, LuPlus } from 'react-icons/lu';
import { CarouselProgress } from '../ui/carousel-progress';
import { useCarouselContext } from '@eliadmin/core/src/components/ui/carousel-context';
import { CarouselControls } from '@eliadmin/core/src/components/ui/carousel-controls';
import { IoIosArrowForward } from 'react-icons/io';
import { CarouselWall } from '../ui/carousel-wall';
import { useBreakpoint } from '@eliadmin/core/src/hooks/use-breakpoint';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export function ServiceTypes({ title, types }: { title: string; types: TDynamicData[] }) {
  const showGrid = types.length <= 2;

  const options = {
    align: 'start' as const,
  };

  const carouselContext = useCarouselContext();

  if (!carouselContext) {
    throw new Error('Hero must be used within a CarouselProvider');
  }

  const { selectedIndex } = carouselContext;

  const isDesktopLG = useBreakpoint('lg');
  const isDesktopXL = useBreakpoint('xl');

  return (
    <section
      className={cn(
        'custom-section',
        'gap-6 bg-primary-foreground px-0 sm:gap-8 lg:gap-12',
        'ea-section__xxl-y',
      )}
    >
      <Heading title={title} className="w-full max-w-[540px] px-5 text-center" tag="h2" />
      <div
        className={cn(
          'custom-container',
          'relative',
          showGrid &&
            'grid grid-cols-1 gap-4 overflow-visible px-5 sm:grid-cols-2 md:gap-5 lg:gap-6 xl:px-0',
          !showGrid && 'pl-5 xl:pl-0',
        )}
      >
        {showGrid ? (
          types.map((type, index) => <TypeCard key={index} type={type} inGrid={true} />)
        ) : (
          <>
            <CarouselWall className="bg-primary-foreground" />
            <Carousel
              options={options}
              className={{
                main: 'w-full overflow-visible',
                container: 'w-full gap-4 overflow-visible sm:gap-5 lg:gap-6',
                wrapper: 'w-full overflow-visible',
                slide:
                  'w-full max-w-[300px] rounded-[8px] sm:max-w-[340px] md:max-w-[400px] lg:max-w-[425px]',
              }}
              slides={types.map(type => (
                <TypeCard key={type.name} type={type} inGrid={false} />
              ))}
            />
          </>
        )}
      </div>
      {!showGrid && (
        <div
          className={cn(
            'custom-container',
            'flex items-center justify-center gap-4 px-5 lg:gap-6 xl:px-0',
          )}
        >
          <CarouselProgress
            selectedIndex={selectedIndex}
            totalSlides={
              (isDesktopXL && types.length - 2) || (isDesktopLG && types.length - 1) || types.length
            }
          />
          <CarouselControls
            buttonProps={{ variant: 'default', size: 'icon' }}
            className={{
              main: 'hidden min-w-[112px] max-w-[112px] sm:flex',
              button: 'border-none bg-white text-black shadow-none hover:bg-white/75',
            }}
            nextIcon={<IoIosArrowForward size={20} />}
            prevIcon={<IoIosArrowForward size={20} className="rotate-180" />}
          />
        </div>
      )}
    </section>
  );
}

function TypeCard({ type, inGrid }: { type: TDynamicData; inGrid?: boolean }) {
  const { t } = useTranslations();
  const cardClass = 'w-full col-span-1 bg-white p-4 md:p-5 lg:p-6 rounded-[8px]';

  const [isActive, setIsActive] = React.useState<boolean>(false);

  if (inGrid) {
    return (
      <div
        className={cn(
          cardClass,
          'flex w-full flex-col items-start justify-start gap-4 md:gap-5 lg:gap-6',
        )}
      >
        <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-[5px] bg-primary-foreground md:h-24 md:w-24 lg:h-[100px] lg:w-[100px]">
          <Image
            src={type?.image?.url ?? ''}
            alt={resolveImageAlt(type?.image, type?.name)}
            width={0}
            height={0}
            className={cn('h-11 w-11 object-contain md:h-14 md:w-14')}
          />
        </div>
        <div className="flex w-full flex-col items-start gap-2 md:gap-3 lg:gap-4">
          <Heading tag="h5" title={type.name} />
          <Paragraph
            innerHtml={type.fields[0]?.value}
            color="muted-foreground"
            size="md"
            className="w-full"
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        cardClass,
        'relative flex h-[320px] flex-col items-start justify-start overflow-hidden sm:h-[360px] md:h-[390px] lg:h-[420px]',
      )}
    >
      <div
        className={cn(
          'flex w-20 flex-none items-center justify-center overflow-hidden rounded-[5px] bg-primary-foreground transition-all duration-300 md:w-24 lg:w-[100px]',
          isActive ? 'h-0 opacity-0' : 'h-20 opacity-100 md:h-24 lg:h-[100px]',
        )}
      >
        <Image
          src={type?.image?.url ?? ''}
          alt={resolveImageAlt(type?.image, type?.name)}
          width={0}
          height={0}
          className={cn('h-11 w-11 object-contain md:h-14 md:w-14')}
        />
      </div>
      <div
        className={cn(
          'flex w-full flex-1 flex-col items-start justify-between gap-4',
          'transition-all duration-300',
          isActive ? 'mt-0' : 'mt-4 sm:mt-5 lg:mt-6',
        )}
      >
        <div className="flex w-full flex-col items-start justify-start gap-4">
          <Heading tag="h5" title={type.name} />
          <Paragraph
            innerHtml={type.fields[0]?.value}
            color="muted-foreground"
            size="md"
            className={cn('w-full', !isActive && 'line-clamp-3')}
          />
        </div>
        <div
          onClick={() => setIsActive(!isActive)}
          className={cn(
            'flex cursor-pointer items-center justify-start gap-4 text-sm font-bold uppercase transition-all hover:opacity-75 sm:text-base',
            isActive ? 'text-primary' : 'text-black',
          )}
        >
          {isActive ? (
            <>
              <LuMinus />
              {t('text604')}
            </>
          ) : (
            <>
              <LuPlus />
              {t('text338')}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
